/**
 * City Configuration and Utilities
 *
 * 도시 관련 설정 및 유틸리티 함수
 */

import { MapPin } from "lucide-react";
import { MAJOR_CITIES, COUNTRIES, type MajorCity } from "@/config/locations";
import type { CityConfig } from '@/types';

/**
 * 국가 코드로 국가명 가져오기 (country-state-city 대체)
 * COUNTRIES 설정에서 직접 조회하여 ~600KB 번들 절감
 */
function getCountryName(countryCode: string): string {
  const country = COUNTRIES.find(c => c.code === countryCode);
  return country?.name.en || "";
}

/**
 * IP 기반으로 위치 정보 가져오기 (국가 코드 + 위도/경도)
 */
export async function getLocationFromIP(): Promise<{
  countryCode: string;
  latitude: number | null;
  longitude: number | null;
}> {
  try {
    const response = await fetch("https://get.geojs.io/v1/ip/geo.json");

    if (response.ok) {
      const data = await response.json();

      return {
        countryCode: data.country_code || "KR",
        latitude: data.latitude ? parseFloat(data.latitude) : null,
        longitude: data.longitude ? parseFloat(data.longitude) : null,
      };
    }
  } catch {
    // IP geolocation 실패 시 기본값 사용
  }

  return { countryCode: "KR", latitude: null, longitude: null };
}

/**
 * 두 지점 간의 거리 계산 (Haversine formula)
 * @returns 거리 (km)
 */
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // 지구 반지름 (km)
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * 로케일에 맞는 도시 이름 가져오기
 * @param city 도시 정보
 * @param locale 로케일 (예: 'ko', 'ja', 'zh-CN', 'zh-TW', 'th', 'vi')
 * @returns 번역된 도시 이름 또는 기본 영어 이름
 */
function getTranslatedCityName(city: MajorCity, locale: string): string {
  if (!city.nameTranslations) return city.name;

  // 정확한 로케일 매칭 시도 (예: "zh-TW")
  if (city.nameTranslations[locale as keyof typeof city.nameTranslations]) {
    return city.nameTranslations[locale as keyof typeof city.nameTranslations]!;
  }

  // 언어 코드만 사용 시도 (예: "zh-CN" -> "zh-CN" 그대로 사용, 없으면 기본값)
  // Note: zh-CN과 zh-TW는 분리되어 있으므로 언어 코드 분리 불필요

  // 기본값: 영어 이름
  return city.name;
}

/**
 * 모든 국가의 주요 도시 데이터를 가져오기 (검색용)
 * @param userLat 사용자 위도 (선택적)
 * @param userLon 사용자 경도 (선택적)
 * @param locale 로케일 (선택적, 도시 이름 번역에 사용)
 * @param userCountryCode 사용자 국가 코드 (선택적, 해당 국가 도시 우선 표시)
 */
export function getAllCities(
  userLat?: number | null,
  userLon?: number | null,
  locale: string = "en",
  userCountryCode?: string | null
): CityConfig[] {
  const allCities: CityConfig[] = [];

  // 모든 국가의 도시 수집
  for (const [countryCode, majorCities] of Object.entries(MAJOR_CITIES)) {
    const countryName = getCountryName(countryCode) || "";

    majorCities.forEach((city) => {
      let distance: number | null = null;
      if (userLat != null && userLon != null) {
        distance = calculateDistance(userLat, userLon, city.latitude, city.longitude);
      }

      const translatedName = getTranslatedCityName(city, locale);

      allCities.push({
        id: `${countryCode}-${city.name.toLowerCase().replace(/\s+/g, "-")}`,
        name: translatedName,
        country: countryName,
        countryCode: countryCode,
        icon: MapPin,
        popular: true,
        distance,
        priority: city.priority, // priority가 없으면 undefined
      });
    });
  }

  // 정렬: 사용자 국가 우선 → priority 있는 도시 먼저 → priority 없는 도시는 거리순
  allCities.sort((a, b) => {
    // 1. 사용자 국가 도시 우선
    if (userCountryCode) {
      const aIsUserCountry = a.countryCode === userCountryCode;
      const bIsUserCountry = b.countryCode === userCountryCode;
      if (aIsUserCountry && !bIsUserCountry) return -1;
      if (!aIsUserCountry && bIsUserCountry) return 1;
    }

    // 2. priority가 있는 도시가 먼저
    const aHasPriority = a.priority != null;
    const bHasPriority = b.priority != null;

    if (aHasPriority && !bHasPriority) return -1;
    if (!aHasPriority && bHasPriority) return 1;

    // 3. 둘 다 priority가 있으면 priority 순서
    if (aHasPriority && bHasPriority) {
      if (a.priority !== b.priority) {
        return a.priority! - b.priority!;
      }
      return a.name.localeCompare(b.name);
    }

    // 4. 둘 다 priority가 없으면 거리순 (거리 없으면 이름순)
    if (a.distance != null && b.distance != null) {
      return a.distance - b.distance;
    }
    if (a.distance != null) return -1;
    if (b.distance != null) return 1;

    return a.name.localeCompare(b.name);
  });

  return allCities;
}

/**
 * 하드코딩된 주요 도시 데이터를 사용하여 지역 정보 가져오기
 * @param countryCode 국가 코드
 * @param userLat 사용자 위도 (선택적, 거리 정렬에 사용)
 * @param userLon 사용자 경도 (선택적, 거리 정렬에 사용)
 * @param locale 로케일 (선택적, 도시 이름 번역에 사용)
 */
export function getCitiesByCountry(
  countryCode: string,
  userLat?: number | null,
  userLon?: number | null,
  locale: string = "en"
): CityConfig[] {
  // 하드코딩된 주요 도시 데이터 확인
  const majorCities = MAJOR_CITIES[countryCode];

  if (!majorCities || majorCities.length === 0) {
    return [];
  }

  const countryName = getCountryName(countryCode) || "";

  const result: CityConfig[] = majorCities.map((city) => {
    // 사용자 위치와 도시 위치가 모두 있으면 거리 계산
    let distance: number | null = null;
    if (userLat != null && userLon != null) {
      distance = calculateDistance(userLat, userLon, city.latitude, city.longitude);
    }

    // 로케일에 맞는 도시 이름 가져오기
    const translatedName = getTranslatedCityName(city, locale);

    return {
      id: `${countryCode}-${city.name.toLowerCase().replace(/\s+/g, "-")}`,
      name: translatedName,
      country: countryName,
      countryCode: countryCode,
      icon: MapPin,
      popular: true,
      distance,
      priority: city.priority, // priority가 없으면 undefined
    };
  });

  // 정렬: priority 있는 도시 먼저 → priority 없는 도시는 거리순
  result.sort((a, b) => {
    // 1. priority가 있는 도시가 먼저
    const aHasPriority = a.priority != null;
    const bHasPriority = b.priority != null;

    if (aHasPriority && !bHasPriority) return -1;
    if (!aHasPriority && bHasPriority) return 1;

    // 2. 둘 다 priority가 있으면 priority 순서
    if (aHasPriority && bHasPriority) {
      if (a.priority !== b.priority) {
        return a.priority! - b.priority!;
      }
      return a.name.localeCompare(b.name);
    }

    // 3. 둘 다 priority가 없으면 거리순 (거리 없으면 이름순)
    if (a.distance != null && b.distance != null) {
      return a.distance - b.distance;
    }
    if (a.distance != null) return -1;
    if (b.distance != null) return 1;

    return a.name.localeCompare(b.name);
  });

  return result;
}
